import { and, eq, gte, inArray, isNotNull } from "drizzle-orm";
import { trace } from "@opentelemetry/api";
import * as turf from "@turf/turf";
import type { FeatureCollection, Point } from "geojson";

import { createLogger } from "@/logger";

const log = createLogger("feed");

import { db } from "@/db/db";
import { feed, permission, workspaceCommandAlias, wsMembershipAvailability } from "@/db/schema";
import { MembershipAvailability } from "@/db/types";

export async function lookupWorkspaceMembershipsWithinRadius(
  latitude: number,
  longitude: number,
  radius: number,
  units: string,
  attributes?: Record<string, string>,
): Promise<{ id: string; title: string }[]> {
  if (!attributes?.workspaceId || !attributes?.workspaceMembershipId) {
    return Promise.reject("workspaceId and workspaceMembershipId are required in attributes");
  }

  const span = trace.getTracer("geoLookup").startSpan("lookupWorkspaceMembershipsWithinRadius");
  span.setAttributes(attributes);
  span.setAttributes({
    latitude,
    longitude,
    radius,
  });

  const turfUnits: turf.Units = units as turf.Units;
  const startTime = performance.now();
  const center = [longitude, latitude];
  const options = { steps: 10, units: turfUnits };
  const circle = turf.circle(center, radius, options);

  const twelveHoursAgo = new Date(Date.now() - 1000 * 60 * 60 * 12);

  const members = await db.query.wsMembershipAvailability.findMany({
    where: and(
      isNotNull(wsMembershipAvailability.mostRecentDeviceLocationLatitude),
      isNotNull(wsMembershipAvailability.mostRecentDeviceLocationLongitude),
      gte(wsMembershipAvailability.mostRecentDeviceLocationUpdatedAt, twelveHoursAgo.toISOString()),
    ),
  });

  const memberQueryEndTime = performance.now();
  const memberPoints = generateFeaturesFromMembers(members);
  const geoCalculationStartTime = performance.now();
  const membersWithinRadius = turf.pointsWithinPolygon(memberPoints, circle);
  const geoCalculationEndTime = performance.now();

  const commandAliasQueryStartTime = performance.now();
  const commandAliasChannels = await db.query.workspaceCommandAlias.findMany({
    where: and(
      eq(workspaceCommandAlias.alias, "dispatch"),
      inArray(
        workspaceCommandAlias.workspaceMembershipId,
        membersWithinRadius.features.map((feature) => feature.properties?.workspaceMembershipId),
      ),
    ),
  });

  const commandAliasQueryEndTime = performance.now();
  const foundIds = commandAliasChannels?.map((channel) => channel.feedId) ?? [];

  const permissionAnd = and(
    inArray(feed.id, foundIds),
    eq(feed.workspaceId, attributes.workspaceId),
    eq(permission.name, "write"),
    eq(permission.enabled, true),
    eq(permission.workspaceMembershipId, attributes.workspaceMembershipId),
  );

  const feeds = await db
    .select({ id: feed.id, title: feed.title })
    .from(feed)
    .innerJoin(permission, eq(feed.id, permission.feedId))
    .where(permissionAnd);

  const overallEndTime = performance.now();

  span.setAttributes({
    numberOfMembers: members.length,
    numberOfMembersWithinRadius: membersWithinRadius.features.length,
    memberQueryTimeMs: memberQueryEndTime - startTime,
    geoCalculationTimeMs: geoCalculationEndTime - geoCalculationStartTime,
    commandAliasQueryTimeMs: commandAliasQueryEndTime - commandAliasQueryStartTime,
    overallTimeMs: overallEndTime - startTime,
  });
  span.end();

  log.debug("GEO", {
    numberOfMembers: members.length,
    numberOfMembersWithinRadius: membersWithinRadius.features.length,
    memberQueryTimeMs: memberQueryEndTime - startTime,
    geoCalculationTimeMs: geoCalculationEndTime - geoCalculationStartTime,
    commandAliasQueryTimeMs: commandAliasQueryEndTime - commandAliasQueryStartTime,
    overallTimeMs: overallEndTime - startTime,
  });

  return feeds?.map((feed) => ({ id: feed.id, title: feed.title }));
}

function generateFeaturesFromMembers(
  members: MembershipAvailability[],
): FeatureCollection<Point, { workspaceMembershipId: string }> {
  return {
    type: "FeatureCollection",
    features: members.map((member) => ({
      type: "Feature",
      geometry: {
        type: "Point",
        coordinates: [member.mostRecentDeviceLocationLongitude, member.mostRecentDeviceLocationLatitude],
      },
      properties: {
        workspaceMembershipId: member.workspaceMembershipId,
      },
    })),
  };
}

// Feel free to delete, useful for performance testing
// function generateFakeLatLongs(count: number, near: [number, number]): FeatureCollection<Point> {
//   return {
//     type: "FeatureCollection",
//     features: Array.from({ length: count }, () => ({
//       type: "Feature",
//       geometry: {
//         type: "Point",
//         coordinates: fakeLatLongNear(near, 10),
//       },
//       properties: {},
//     })),
//   };
// }

// function fakeLatLongNear(near: [number, number], radius: number): [number, number] {
//   const [longitude, latitude] = near;
//   return [longitude + (Math.random() * 2 - 1) * radius, latitude + (Math.random() * 2 - 1) * radius];
// }
